import { getColorScheme, type ColorScheme } from '../../theme/colorSchemes';

export interface FilterChipProps {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
  colorScheme?: ColorScheme;
  size?: 'sm' | 'md';
  /** Slug for data-testid attribute (e.g., "all", "interactive_plan") */
  slug?: string;
  /** Truncate label to this many characters (shows full label on hover) */
  maxLabelLength?: number;
}

/**
 * Unified filter chip component used across both Categories and Accounts views.
 * Supports theming via colorScheme prop for consistent yet distinct styling.
 */
export function FilterChip({
  label,
  count,
  active,
  onClick,
  colorScheme = 'blue',
  size = 'md',
  slug,
  maxLabelLength
}: FilterChipProps) {
  const truncated = maxLabelLength && label.length > maxLabelLength;
  const displayLabel = truncated ? label.slice(0, maxLabelLength) + '…' : label;
  // Size variants
  const sizeClasses = size === 'sm' 
    ? 'px-2 py-0.5 text-xs'
    : 'px-3 py-1 text-sm';

  // Get color scheme from centralized theme
  const theme = getColorScheme(colorScheme);
  
  const baseClasses = 'inline-flex items-center gap-1.5 font-medium rounded-lg transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';
  const toneClasses = active ? theme.chipActive : theme.chipInactive;
  const countToneClasses = active ? theme.chipActiveCount : theme.chipInactiveCount;

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${sizeClasses} ${toneClasses}`}
      data-testid={slug ? `filter-${slug}` : undefined}
      title={truncated ? label : undefined}
    >
      <span>{displayLabel}</span>
      {count !== undefined && (
        <span className={`inline-flex h-[18px] min-w-[18px] px-1 items-center justify-center rounded-md text-[11px] ${countToneClasses}`}>
          {count}
        </span>
      )}
    </button>
  );
}
